import React, { Component, ErrorInfo } from 'react';
import { Button } from 'antd';
import { ReloadOutlined, HomeOutlined, BugOutlined } from '@ant-design/icons';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    /loading chunk \d+ failed/i.test(error.message) ||
    /loading css chunk \d+ failed/i.test(error.message) ||
    /failed to fetch dynamically imported module/i.test(error.message)
  );
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, info);
    this.props.onError?.(error, info);
  }

  handleReload = () => window.location.reload();
  handleGoHome = () => (window.location.href = '/');

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const { isChunkError, error } = this.state;

    if (isChunkError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-5">
              <ReloadOutlined className="text-2xl text-amber-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Update available</h2>
            <p className="text-sm text-gray-500 mb-6">
              A new version of BRAI was deployed. Reload the page to get the latest version.
            </p>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              size="large"
              onClick={this.handleReload}
              className="w-full"
            >
              Reload Page
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
            <BugOutlined className="text-2xl text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-sm text-gray-500 mb-2">
            An unexpected error occurred. Our team has been notified.
          </p>
          {error && (
            <p className="text-xs text-gray-400 mb-6 font-mono bg-gray-100 rounded-lg p-3 text-left break-all">
              {error.message}
            </p>
          )}
          <div className="flex gap-3">
            <Button
              icon={<HomeOutlined />}
              size="large"
              onClick={this.handleGoHome}
              className="flex-1"
            >
              Go Home
            </Button>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              size="large"
              onClick={this.handleReload}
              className="flex-1"
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
